import { all, call, debounce, put, select, takeEvery } from '@redux-saga/core/effects';
import Cookies from 'js-cookie';
import {
    IDeal,
    IDealProperty,
} from '@/Api/Deal';
import AddressVersionEnum from '@/Api/Deal/AddressVersionEnum';
import IDealQuote from '@/Api/Deal/IDealQuote';
import {
    parseDeal,
    parseDealQuote,
    parseProperty,
} from '@/Api/Deal/parsers';
import {
    dealAddRequest,
    dealCompleteRequest,
    dealFileAddRequest,
    dealGetRequest,
    dealPropertiesAddRequest,
    dealPropertyDeleteRequest,
    dealPropertyGnafAddressDetailRequest,
    dealPropertyUpdateRequest,
    dealQuotesListRequest,
    dealReferredByRequest,
    dealUpdateRequest,
} from '@/Api/Deal/requests';
import UiSourceEnum from '@/Api/Deal/UiSourceEnum';
import { IFetchResponse } from '@/utilities/fetch';
import {
    IDealAddAction,
    IDealAddressSearchAction,
    IDealCompleteAction,
    IDealGetAction,
    IDealPropertyGnafAddressDetailAction,
    IDealQuotesListAction,
    IDealSendAction,
    IDealValueSetAction,
    IPropertyAddAction,
    IPropertyDeleteAction,
    IPropertySendAction,
    IPropertyValueSetAction,
    dealAddressSearchResultsSetAction,
    dealPropertyGnafAddressDetailLoadingClearAction,
    dealQuotesSetAction,
    dealSendAction,
    dealSetAction,
    propertySendAction,
    propertySetAction,
} from './actions';
import LeadActionsEnum from './ActionsEnum';
import {
    dealSelector,
    propertySelector,
} from './selectors';
import IToken from '@/Auth/IToken';
import history from '@/history';
import { decodedTokenSelector } from '@/Auth/selectors';
import {
    doLogin,
    doLogout,
} from '@/Auth/sagas';
import { addressSearchRequest } from '@/Api/AddressSearch/requests';
import { parseAddressSearchResult } from '@/Api/AddressSearch/parsers';
import IAddressSearchResult from '@/Api/AddressSearch/IAddressSearchResult';

interface IReferrer {
    token: string;
}

function* dealAdd(action: IDealAddAction): Iterator<unknown> {
    const landingPage: string = Cookies.get('landing');

    const deal: IDeal = yield select(dealSelector);

    const rawDeal: IFetchResponse = yield call(dealAddRequest, {
        ...deal,
        landingPage,
        uiSource: UiSourceEnum.ExpressQuote,
    });

    const token: string = rawDeal.headers.get('Funding-Auth-Token');

    yield call(doLogin, token);

    const parsedDeal: IDeal = parseDeal(rawDeal.body);
    yield put(dealSetAction(parsedDeal));

    const referrerCookie: string = Cookies.get('dealReferrer');

    if (referrerCookie) {
        const referrer: IReferrer = JSON.parse(referrerCookie);
        if (referrer.token) {
            yield call(dealReferredByRequest, parsedDeal.uuid, referrer.token);
        }
    }

    history.push(action.successUrl);

    const property: IDealProperty = {
        addressVersion: AddressVersionEnum.GNAF_V1,
        currentDebt: null,
        errors: {},
        estimatedValue: null,
        postcode: null,
        state: null,
        streetAddress: null,
        streetName: null,
        streetNumber: null,
        streetType: null,
        suburb: null,
        unitNumber: null,
        zoneType: null,
    };

    const rawProperty: IFetchResponse = yield call(dealPropertiesAddRequest, parsedDeal, property);
    const parsedProperty: IDealProperty = parseProperty(rawProperty.body);
    yield put(propertySetAction(parsedProperty));
}

function* dealComplete(action: IDealCompleteAction): Iterator<unknown> {
    const deal: IDeal = yield select(dealSelector);

    if (action.supportingDocuments) {
        for (const supportingDocument of Array.from(action.supportingDocuments)) {
            try {
                yield call(dealFileAddRequest, deal, supportingDocument);
            } catch {
                // We don't want a file add request failure to prevent us from completing the deal
            }
        }
    }

    yield call(dealCompleteRequest, deal, action.autoQuote);
    let dealQuotes: IDealQuote[] = [];

    if (action.autoQuote) {
        const dealQuotesListResponse: IFetchResponse = yield call(dealQuotesListRequest, deal.uuid);
        dealQuotes = yield Promise.all(dealQuotesListResponse.body.map(parseDealQuote));
    }

    if (action.autoQuote && dealQuotes.length === 1) {
        yield put(dealQuotesSetAction(dealQuotes));
        history.push(action.quoteUrl);
    } else {
        doLogout();
        history.push(action.thanksUrl);
    }
}

function* dealGet(_action: IDealGetAction): Iterator<unknown> {
    const decodedToken: IToken = yield select(decodedTokenSelector);

    if (decodedToken.dealUuid) {
        const rawDeal: IFetchResponse = yield call(dealGetRequest, decodedToken.dealUuid);
        const deal: IDeal = parseDeal(rawDeal.body);
        yield put(dealSetAction(deal));
    }
}

function* dealSend(_action: IDealSendAction): Iterator<unknown> {
    const deal: IDeal = yield select(dealSelector);
    if (deal && deal.uuid) {
        yield call(dealUpdateRequest, deal);
    }
}

function* dealValueSet(_action: IDealValueSetAction): Iterator<unknown> {
    yield put(dealSendAction());
}

function* dealQuotesList(action: IDealQuotesListAction): Iterator<unknown> {
    const dealQuotesListResponse: IFetchResponse = yield call(dealQuotesListRequest, action.dealUuid);
    const dealQuotes: IDealQuote[] = yield Promise.all(dealQuotesListResponse.body.map(parseDealQuote));
    yield put(dealQuotesSetAction(dealQuotes));
}

function* propertyAdd(_action: IPropertyAddAction): Iterator<unknown> {
    const deal: IDeal = yield select(dealSelector);
    const property: IDealProperty = {
        addressVersion: AddressVersionEnum.GNAF_V1,
        currentDebt: null,
        errors: {},
        estimatedValue: null,
        postcode: null,
        state: null,
        streetAddress: null,
        streetName: null,
        streetNumber: null,
        streetType: null,
        suburb: null,
        unitNumber: null,
        zoneType: null,
    };
    const rawProperty: IFetchResponse = yield call(dealPropertiesAddRequest, deal, property);
    const parsedProperty: IDealProperty = parseProperty(rawProperty.body);
    yield put(propertySetAction(parsedProperty));
}

function* propertyDelete(action: IPropertyDeleteAction): Iterator<unknown> {
    yield call(dealPropertyDeleteRequest, action.uuid);
}

function* propertySend(action: IPropertySendAction): Iterator<unknown> {
    const property: IDealProperty = yield select(propertySelector, action.uuid);

    if (property && property.uuid) {
        yield call(dealPropertyUpdateRequest, property);
    }
}

function* propertyValueSet(action: IPropertyValueSetAction): Iterator<unknown> {
    yield put(propertySendAction(action.uuid));
}

function* dealAddressSearch(action: IDealAddressSearchAction): Iterator<unknown> {
    const addressSearchResponse: IFetchResponse = yield call(addressSearchRequest, action.keyword);
    const searchResults: IAddressSearchResult[] = yield Promise.all(addressSearchResponse.body.map(parseAddressSearchResult));
    yield put(dealAddressSearchResultsSetAction(searchResults));
}

function* dealPropertyGnafAddressDetail(action: IDealPropertyGnafAddressDetailAction): Iterator<unknown> {
    const gnafAddressDetailResponse: IFetchResponse = yield call(dealPropertyGnafAddressDetailRequest, action.propertyUuid, action.gnafAddressDetailPid);
    const property: IDealProperty = parseProperty(gnafAddressDetailResponse.body);
    yield put(propertySetAction(property));
    yield put(dealPropertyGnafAddressDetailLoadingClearAction());
}

export function* LeadSagas(): Iterator<unknown> {
    yield all([
        takeEvery(LeadActionsEnum.DealAdd, dealAdd),
        takeEvery(LeadActionsEnum.DealComplete, dealComplete),
        takeEvery(LeadActionsEnum.DealGet, dealGet),
        debounce(500, LeadActionsEnum.DealSend, dealSend),
        takeEvery(LeadActionsEnum.DealValueSet, dealValueSet),

        takeEvery(LeadActionsEnum.DealQuotesList, dealQuotesList),

        debounce(200, LeadActionsEnum.DealAddressSearch, dealAddressSearch),
        takeEvery(LeadActionsEnum.PropertyGnafAddressDetail, dealPropertyGnafAddressDetail),

        takeEvery(LeadActionsEnum.PropertiesAdd, propertyAdd),

        takeEvery(LeadActionsEnum.PropertyDelete, propertyDelete),
        debounce(500, LeadActionsEnum.PropertySend, propertySend),
        takeEvery(LeadActionsEnum.PropertyValueSet, propertyValueSet),
    ]);
}
